import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/** Minimal shape of the cordova-plugin-haptic JS API we depend on. */
interface HapticPlugin {
  sendHapticFeedback(
    androidType: string,
    iosType: string,
    errorCallback?: (message: string) => void,
  ): void;
}

/**
 * Tactile feedback for the drag-a-customer-to-calendar gesture, via
 * cordova-plugin-haptic.
 *
 * All calls are best-effort and fail safe: the service no-ops on web / desktop /
 * `ng serve` (the plugin is absent there) and never throws into the drag/drop
 * flow. Android constants are chosen for broad reach — `LONG_PRESS` works on all
 * Android versions, `CONFIRM` on Android 11+ — so haptics fire even on devices
 * below Android 14. See ai-docs/2026-05-21-drag-drop-haptic-feedback-design.md.
 */
@Injectable({ providedIn: 'root' })
export class HapticsService {
  /** Subtle tap the moment the drag is armed after the long-press. */
  dragReady(): void {
    // LONG_PRESS: HapticFeedbackConstants, API 3 — fires on all Android versions.
    this.fire('LONG_PRESS', 'ImpactLight');
  }

  /** Confirmation when a customer is dropped on a valid calendar slot. */
  dropAccepted(): void {
    // A double LONG_PRESS reads as a distinct "landed" cue and is reliable on all
    // Android devices. We avoid the semantically-nicer CONFIRM (API 30): some HALs
    // (e.g. Samsung SM-M526B) silently no-op the newer HapticFeedbackConstants,
    // whereas LONG_PRESS (API 3) is universal. iOS gets a single Success — the
    // second pulse passes an unrecognised iosType so it is Android-only.
    this.fire('LONG_PRESS', 'Success');
    setTimeout(() => this.fire('LONG_PRESS', ''), 100);
  }

  /** A light confirmation when an order reaches a discount tier or earns free goods. */
  offerEarned(): void {
    this.fire('LONG_PRESS', 'Success');
  }

  private fire(androidType: string, iosType: string): void {
    const plugin = this.resolvePlugin();
    if (!plugin) return; // web / desktop / ng serve, or plugin not installed
    try {
      plugin.sendHapticFeedback(androidType, iosType);
    } catch (error) {
      // Silent in production (console-cleanup roadmap); dev-only for native debugging.
      if (!environment.production) {
        console.debug('[haptics] sendHapticFeedback failed', error);
      }
    }
  }

  private resolvePlugin(): HapticPlugin | undefined {
    const cordovaGlobal = (
      window as unknown as {
        cordova?: { plugins?: { hapticPlugin?: HapticPlugin } };
      }
    ).cordova;
    return cordovaGlobal?.plugins?.hapticPlugin;
  }
}
